import React, { useState } from 'react';
import { Button } from '../Button';
import { EditIcon, DeleteIcon, PlusIcon, SaveIcon, QuestionMarkCircleIcon } from './DashboardIcons';

interface FAQ {
    id: number;
    question: string;
    answer: string;
}

interface LegalArea {
    id: string;
    title: string;
    icon: string;
    description: string;
}

const initialFaqs: FAQ[] = [
    { id: 1, question: '¿Qué posibilidades de éxito tengo al recurrir una multa?', answer: 'Las posibilidades son altas, especialmente si existen defectos de forma...' },
    { id: 2, question: '¿Cuánto tiempo tarda el proceso de recurso?', answer: 'El proceso puede variar dependiendo de la administración y el país...' },
];

const initialLegalAreas: LegalArea[] = [
    { id: 'traffic-fines', title: 'Multas de Tráfico', icon: 'directions_car', description: 'Recurra de forma experta multas y sanciones...' },
    { id: 'admin-sanctions', title: 'Sanciones Administrativas', icon: 'gavel', description: 'Impugne sanciones administrativas y penalizaciones...' },
];

export const FaqView: React.FC = () => {
    const [faqs, setFaqs] = useState<FAQ[]>(initialFaqs);
    const [legalAreas, setLegalAreas] = useState<LegalArea[]>(initialLegalAreas);
    
    const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
    const [editingArea, setEditingArea] = useState<LegalArea | null>(null);

    // FAQ Handlers
    const handleEditFaq = (faq: FAQ | null) => setEditingFaq(faq ? {...faq} : { id: Date.now(), question: '', answer: '' });
    const handleSaveFaq = () => {
        if (!editingFaq) return;
        setFaqs(prev => {
            const exists = prev.some(f => f.id === editingFaq.id);
            if (exists) {
                return prev.map(f => f.id === editingFaq.id ? editingFaq : f);
            }
            return [...prev, editingFaq];
        });
        setEditingFaq(null);
    };
    const handleDeleteFaq = (id: number) => {
        if (window.confirm('¿Seguro que quieres eliminar esta pregunta?')) {
            setFaqs(prev => prev.filter(f => f.id !== id));
        }
    };

    // Legal Area Handlers
    const handleEditArea = (area: LegalArea | null) => setEditingArea(area ? {...area} : { id: `area-${Date.now()}`, title: '', icon: '', description: '' });
    const handleSaveArea = () => {
        if (!editingArea) return;
        setLegalAreas(prev => {
            const exists = prev.some(a => a.id === editingArea.id);
            if (exists) {
                return prev.map(a => a.id === editingArea.id ? editingArea : a);
            }
            return [...prev, editingArea];
        });
        setEditingArea(null);
    };
    const handleDeleteArea = (id: string) => {
        if (window.confirm('¿Seguro que quieres eliminar esta área legal?')) {
            setLegalAreas(prev => prev.filter(a => a.id !== id));
        }
    };


    return (
        <div className="p-8 bg-white rounded-lg shadow-sm">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center mb-6"><QuestionMarkCircleIcon /><span className="ml-2">Gestión de Contenido FAQ</span></h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* FAQ Management */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-700">Preguntas Frecuentes</h2>
                        <Button onClick={() => handleEditFaq(null)} size="small" variant="primary" className="bg-blue-600 text-white"><PlusIcon /></Button>
                    </div>
                    {editingFaq && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <input type="text" placeholder="Pregunta" value={editingFaq.question} onChange={e => setEditingFaq({...editingFaq, question: e.target.value})} className="w-full p-2 border rounded mb-2" />
                            <textarea placeholder="Respuesta" value={editingFaq.answer} onChange={e => setEditingFaq({...editingFaq, answer: e.target.value})} className="w-full p-2 border rounded" rows={3}></textarea>
                            <div className="flex justify-end gap-2 mt-2">
                                <Button onClick={() => setEditingFaq(null)} size="small" variant="outline" className="border-gray-300 text-gray-700">Cancelar</Button>
                                <Button onClick={handleSaveFaq} size="small" variant="success"><SaveIcon /></Button>
                            </div>
                        </div>
                    )}
                    <ul className="space-y-2">
                        {faqs.map(faq => (
                            <li key={faq.id} className="p-3 bg-gray-50 rounded-lg flex justify-between items-start">
                                <div>
                                    <p className="font-semibold">{faq.question}</p>
                                    <p className="text-sm text-gray-600 truncate">{faq.answer}</p>
                                </div>
                                <div className="flex space-x-2 flex-shrink-0 ml-4">
                                    <button onClick={() => handleEditFaq(faq)} className="text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                    <button onClick={() => handleDeleteFaq(faq.id)} className="text-red-500 hover:text-red-700"><DeleteIcon /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Legal Areas Management */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-700">Áreas Legales</h2>
                        <Button onClick={() => handleEditArea(null)} size="small" variant="primary" className="bg-blue-600 text-white"><PlusIcon /></Button>
                    </div>
                     {editingArea && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <input type="text" placeholder="Título del Área" value={editingArea.title} onChange={e => setEditingArea({...editingArea, title: e.target.value})} className="w-full p-2 border rounded mb-2" />
                            <input type="text" placeholder="Icono (Material Icons)" value={editingArea.icon} onChange={e => setEditingArea({...editingArea, icon: e.target.value})} className="w-full p-2 border rounded mb-2" />
                            <textarea placeholder="Descripción corta" value={editingArea.description} onChange={e => setEditingArea({...editingArea, description: e.target.value})} className="w-full p-2 border rounded" rows={2}></textarea>
                            <div className="flex justify-end gap-2 mt-2">
                                <Button onClick={() => setEditingArea(null)} size="small" variant="outline" className="border-gray-300 text-gray-700">Cancelar</Button>
                                <Button onClick={handleSaveArea} size="small" variant="success"><SaveIcon /></Button>
                            </div>
                        </div>
                    )}
                    <ul className="space-y-2">
                        {legalAreas.map(area => (
                            <li key={area.id} className="p-3 bg-gray-50 rounded-lg flex justify-between items-start">
                                <div>
                                    <p className="font-semibold flex items-center"><span className="material-icons-outlined text-base mr-2">{area.icon}</span>{area.title}</p>
                                    <p className="text-sm text-gray-600">{area.description}</p>
                                </div>
                                <div className="flex space-x-2 flex-shrink-0 ml-4">
                                    <button onClick={() => handleEditArea(area)} className="text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                    <button onClick={() => handleDeleteArea(area.id)} className="text-red-500 hover:text-red-700"><DeleteIcon /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};